import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Search, X, ArrowRight, List, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import CommunityMap from "@/components/CommunityMap";

const WEEKDAYS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];

export default function Communities() {
  const { t } = useI18n();
  const [communities, setCommunities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({ country: "", city: "", postal_code: "", day: "" });

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.country) params.country = filters.country;
    if (filters.city) params.city = filters.city;
    if (filters.postal_code) params.postal_code = filters.postal_code;
    if (filters.day) params.day = filters.day;
    api.get("/communities", { params }).then((r) => setCommunities(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/communities/meta/countries").then((r) => setCountries(r.data));
    load();
  }, []); // eslint-disable-line

  const clear = () => { setFilters({ country: "", city: "", postal_code: "", day: "" }); setTimeout(load, 0); };

  return (
    <div>
      <section className="bg-th-blue th-pattern text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display font-bold text-4xl sm:text-5xl">{t("find_title")}</h1>
          <p className="mt-3 text-white/75 max-w-2xl text-lg">{t("find_subtitle")}</p>
          <Link to="/candidatar-grupo" data-testid="communities-apply-group-btn" className="inline-block mt-5">
            <Button className="bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold rounded-full px-6">{t("apply_cta")}<ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>

          <div className="mt-8 bg-white rounded-2xl p-5 shadow-xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("filter_country")}</label>
              <Select value={filters.country || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, country: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="filter-country" className="mt-1 text-th-blue"><SelectValue placeholder={t("filter_all")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filter_all")}</SelectItem>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Dia de oração</label>
              <Select value={filters.day || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, day: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="filter-day" className="mt-1 text-th-blue"><SelectValue placeholder={t("filter_all")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filter_all")}</SelectItem>
                  {WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("filter_city")}</label>
              <Input data-testid="filter-city" className="mt-1 text-th-blue" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} placeholder={t("filter_city")} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("filter_postal")}</label>
              <Input data-testid="filter-postal" className="mt-1 text-th-blue" value={filters.postal_code} onChange={(e) => setFilters((f) => ({ ...f, postal_code: e.target.value }))} placeholder="0000" />
            </div>
            <div className="flex gap-2">
              <Button onClick={load} data-testid="search-btn" className="flex-1 bg-th-blue text-white hover:bg-th-bluelight"><Search className="w-4 h-4 mr-1" />{t("search")}</Button>
              <Button onClick={clear} variant="outline" data-testid="clear-btn" className="border-slate-300"><X className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center justify-end mb-6">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="view-toggle">
            <button onClick={() => setView("list")} data-testid="view-list-btn" className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "list" ? "bg-th-blue text-white" : "text-slate-500"}`}><List className="w-4 h-4" />{t("view_list")}</button>
            <button onClick={() => setView("map")} data-testid="view-map-btn" className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === "map" ? "bg-th-blue text-white" : "text-slate-500"}`}><MapIcon className="w-4 h-4" />{t("view_map")}</button>
          </div>
        </div>
        {loading ? (
          <p className="text-slate-500">{t("loading")}</p>
        ) : view === "map" ? (
          <CommunityMap communities={communities} />
        ) : (
          <div className="space-y-10" data-testid="communities-grid">
            {Object.keys(communities.reduce((a, c) => ({ ...a, [c.country || "—"]: 1 }), {})).sort().map((country) => {
              const list = communities.filter((c) => (c.country || "—") === country);
              return (
                <div key={country} data-testid={`country-group-${country}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <h2 className="font-display font-semibold text-2xl text-th-blue">{country}</h2>
                    <span className="text-th-gold font-semibold text-sm">{list.length}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map((c) => (
                      <div key={c.id} data-testid={`community-card-${c.id}`} className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-th-blue hover:shadow-lg transition-all group">
                        <h3 className="font-display font-semibold text-xl text-th-blue">{c.name}</h3>
                        <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-th-gold mt-0.5 shrink-0" />{[c.parish, c.address, c.city].filter(Boolean).join(", ")}</div>
                          {(c.periodicity || c.prayer_day || c.prayer_time || c.schedule) && <div className="flex items-start gap-2"><Clock className="w-4 h-4 text-th-gold mt-0.5 shrink-0" />{[c.periodicity, c.prayer_day, c.prayer_time].filter(Boolean).join(" · ") || c.schedule}</div>}
                        </div>
                        <Link to={`/comunidades/${c.id}`}>
                          <Button variant="ghost" className="mt-4 px-0 text-th-blue hover:text-th-gold hover:bg-transparent" data-testid={`community-view-${c.id}`}>
                            {t("view_community")} <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {communities.length === 0 && <p className="text-slate-500">—</p>}
          </div>
        )}
      </section>
    </div>
  );
}
